// A face on the draft board wincing when the player it wanted is taken just
// before its pick (B-529), as a hook of its own so its timing is tested
// without a room.

import { useEffect, useRef, useState } from 'react';
import type { DraftView } from '@fsp/domain';

/** How long a face on the clock winces before it goes back to thinking. */
export const WINCE_MS = 3000;

/**
 * The seat now on the clock winces a moment when the pick just made took the
 * player it wanted (B-529): a bot's, as the room says (`current.sniped`); my
 * own, when it was the top of my queue, which only this browser knows. Never
 * at a seat's own pick, and only for a pick just made: an undo takes the count
 * back and a refresh leaves it. The team it is said of, for `WINCE_MS`; null
 * otherwise.
 */
export function useWince(view: DraftView): string | null {
  const [wince, setWince] = useState<{ teamId: string; at: number } | null>(null);
  const overall = view.lastPick?.overall ?? null;
  const seen = useRef(overall);
  // My queue's top before the pick: the pick's own answer takes him off it,
  // so this is read by the effect below before the next one moves it on.
  const myTop = useRef<string | null>(view.myQueue[0]?.id ?? null);
  const latest = useRef(view);
  latest.current = view;
  useEffect(() => {
    const was = seen.current;
    seen.current = overall;
    if (overall === null || (was !== null && overall <= was)) return;
    const { current, lastPick } = latest.current;
    const taken = lastPick?.player?.id ?? null;
    if (current === null || taken === null || lastPick?.teamId === current.teamId) return;
    if (current.sniped || (current.isMine && myTop.current === taken))
      setWince({ teamId: current.teamId, at: Date.now() });
  }, [overall]);
  useEffect(() => {
    myTop.current = view.myQueue[0]?.id ?? null;
  }, [view.myQueue]);
  // ITS OWN TIMER, so a view read again inside it never keeps it on.
  useEffect(() => {
    if (wince === null) return;
    const t = window.setTimeout(() => setWince(null), WINCE_MS);
    return () => window.clearTimeout(t);
  }, [wince]);
  return wince?.teamId ?? null;
}
